import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useState,
} from 'react';
import styles from './styles.less';

export const rateConfig = [
  { label: '正常', value: 1 },
  { label: '2X', value: 2 },
  { label: '4X', value: 4 },
  { label: '8X', value: 8 },
  { label: '16X', value: 16 },
];

const Playback = forwardRef(
  ({ playbackTimes, playUrl, currentDate, onSeek, startTime }, ref) => {
    const containerRef = useRef(null);
    const jessibucaRef = useRef(null);
    const isPlaybackPauseRef = useRef(false);

    const playbackTimesRef = useRef(playbackTimes);
    const currentDateRef = useRef(currentDate);
    const playUrlRef = useRef(playUrl);
    const startTimeRef = useRef(startTime);
    const [playerKey, setPlayerKey] = useState(0);

    useEffect(() => {
      playbackTimesRef.current = playbackTimes;
    }, [playbackTimes]);

    useEffect(() => {
      currentDateRef.current = currentDate;
    }, [currentDate]);

    useEffect(() => {
      playUrlRef.current = playUrl;
    }, [playUrl]);

    useEffect(() => {
      startTimeRef.current = startTime;
    }, [startTime]);

    const getPlayerConfig = () => {
      const times = playbackTimesRef.current || [];
      return {
        playType: 'playbackTF',
        videoBuffer: 0.2,
        decoder: 'decoder-pro.js',
        isResize: true, // 开启自适应
        text: '',
        loadingText: '直播加载中，请稍等...',
        showPerformance: false,
        isFFmpegSIMD: false,
        useMThreading: false,
        showBandwidth: false,
        showPlaybackOperate: false,
        operateBtns: {
          fullscreen: false, // 移动端可能需要全屏按钮
          screenshot: false,
          play: false, // 保留播放按钮用于 UI 互动
          audio: false,
          zoom: false,
        },
        playbackConfig: {
          playList: times,
          showControl: false,
          isUseFpsRender: false,
          isCacheBeforeDecodeForFpsRender: false,
          isPlaybackPauseClearCache: true,
          showRateBtn: false,
          supportWheel: false, // 禁用滚轮防触碰干扰
          uiUsePlaybackPause: false,
          isUseLocalCalculateTime: false,
          rateConfig: rateConfig,
          useMSE: true,
          useWCS: true,
          useSIMD: false,
        },
        isNotMute: false,
        playbackForwardMaxRateDecodeIFrame: 4,
        useWebGPU: false,
        playbackCheckStreamEnd: true,
        supportHls265: true,
      };
    };

    const setupEventListeners = () => {
      const player = jessibucaRef.current;
      if (!player) return;

      player.on('play', () => {
        if (startTimeRef.current) {
          setTimeout(() => {
            if (jessibucaRef.current) {
              jessibucaRef.current.setPlaybackStartTime(
                Math.floor(parseInt(startTimeRef.current) / 1000),
              );
            }
          }, 500);
        }
      });

      player.on('playbackPreRateChange', (rate) => {
        player.forward(rate);
      });

      player.on('playbackSeek', (data) => {
        let baseDate;
        if (currentDateRef.current) {
          baseDate = new Date(currentDateRef.current);
        } else if (
          playbackTimesRef.current &&
          playbackTimesRef.current.length > 0
        ) {
          baseDate = new Date(playbackTimesRef.current[0].start * 1000);
        } else {
          baseDate = new Date();
        }
        const currentTime = baseDate.setHours(
          data.hour,
          data.min,
          data.second,
          0,
        );
        if (onSeek) onSeek(data, currentTime);
        player.setPlaybackStartTime(Math.floor(currentTime / 1000));
      });

      player.on('playbackEnd', () => {
        console.error('playbackEnd: 播放结束');
      });

      player.on(
        window.JessibucaPro?.EVENTS?.playFailedAndPaused ||
          'playFailedAndPaused',
        (error) => {
          console.error('播放异常:', error);
        },
      );
    };

    const createPlayer = () => {
      if (typeof window.JessibucaPro === 'undefined' || !containerRef.current) {
        console.warn('JessibucaPro 尚未加载');
        return;
      }
      const config = getPlayerConfig();
      jessibucaRef.current = new window.JessibucaPro({
        ...config,
        container: containerRef.current,
      });
      setupEventListeners();
      return jessibucaRef.current;
    };

    const play = () => {
      if (!playUrlRef.current || !jessibucaRef.current) return;
      if (isPlaybackPauseRef.current) {
        jessibucaRef.current.playbackResume();
      } else {
        jessibucaRef.current.playback(playUrlRef.current).catch((error) => {
          console.error('播放异常:', error);
        });
      }
      isPlaybackPauseRef.current = false;
    };

    const pause = () => jessibucaRef.current?.pause();
    const playbackPause = () => {
      jessibucaRef.current?.playbackPause();
      isPlaybackPauseRef.current = true;
    };
    const forward = (rate) => jessibucaRef.current?.forward(rate);
    const destroyPlayer = () => {
      if (jessibucaRef.current) {
        jessibucaRef.current.destroy().then(() => {
          jessibucaRef.current = null;
          createPlayer();
        });
      }
    };
    const cancelMute = () => jessibucaRef.current?.setVolume(100);
    const mute = () => jessibucaRef.current?.setVolume(0);
    const setRotate = (deg) => jessibucaRef.current?.setRotate(deg);
    const resize = () => jessibucaRef.current?.resize();
    const replay = () => {
      destroyPlayer();
      createPlayer();
      play();
    };
    const currentTimeScroll = () =>
      jessibucaRef.current?.playbackCurrentTimeScroll();

    const screenshot = (cb) => {
      const container = containerRef.current;
      if (!container) return;
      const video = container.querySelector('video');
      if (!video) return;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const drawAndExport = () => {
        canvas.width = video.videoWidth || 0;
        canvas.height = video.videoHeight || 0;
        if (canvas.width && canvas.height) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => cb && cb(blob), 'image/jpeg', 1);
        }
      };
      if (video.readyState >= video.HAVE_CURRENT_DATA) {
        drawAndExport();
      } else {
        const handler = () => {
          drawAndExport();
          video.removeEventListener('loadeddata', handler);
        };
        video.addEventListener('loadeddata', handler);
      }
    };

    const updatePlaybackList = (times) => {
      if (!times || times.length === 0) return;
      playbackTimesRef.current = times;
      setPlayerKey((prev) => prev + 1);
    };

    useImperativeHandle(ref, () => ({
      play,
      pause,
      playbackPause,
      screenshot,
      replay,
      destroyPlayer,
      currentTimeScroll,
      forward,
      cancelMute,
      mute,
      setRotate,
      resize,
      getPlayerInstance: () => jessibucaRef.current,
      updatePlaybackList,
    }));

    useEffect(() => {
      if (playUrl && jessibucaRef.current) {
        const timer = setTimeout(() => play(), 100);
        return () => clearTimeout(timer);
      }
    }, [playUrl]);

    useEffect(() => {
      if (playbackTimes && playbackTimes.length > 0) {
        playbackTimesRef.current = playbackTimes;
        if (jessibucaRef.current) setPlayerKey((prev) => prev + 1);
      }
    }, [playbackTimes]);

    useEffect(() => {
      const player = createPlayer();
      if (player && playUrlRef.current) {
        setTimeout(() => play(), 100);
      }
      return () => {
        if (player) player.destroy();
      };
    }, [playerKey]);

    return (
      <div style={{ width: '100%', height: '100%' }}>
        <div
          id="container"
          key={playerKey}
          className={styles.playBox}
          ref={containerRef}
        ></div>
      </div>
    );
  },
);

export default Playback;
